import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Shield, Eye, Heart, Lightbulb, Mail, Star, Building2, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';

const values = [
  { icon: Shield, title: 'Trust', description: 'Building credibility through verified achievements and transparent processes.' },
  { icon: Eye, title: 'Transparency', description: 'Clear, honest communication in all interactions between candidates and organizations.' },
  { icon: Heart, title: 'Integrity', description: 'Maintaining the highest ethical standards in skill discovery and verification.' },
  { icon: Lightbulb, title: 'Innovation', description: 'Continuously improving how professional capability is communicated and discovered.' },
];

const freeTierFeatures = [
  'Create a professional profile',
  'Add skills, experience & education',
  'Upload portfolio links',
  'Apply for opportunities',
  'Basic profile visibility',
];

const proTierFeatures = [
  'Everything in Free, plus:',
  'Verified profile badge',
  'Referee validation & verification',
  'Achievement verification',
  'Downloadable PDF profiles',
  'Increased recruiter visibility',
  'Profile engagement insights',
  'Priority in search results',
];

const enterpriseTierFeatures = [
  'Verified candidate access',
  'Advanced search & filtering',
  'Bulk candidate discovery',
  'Direct engagement tools',
  'Organisation profiles & branding',
  'Custom integrations & API access',
  'Dedicated account manager',
  'Priority support',
];

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const navigate = useNavigate();

  // Detect if user is logged in (from localStorage); used to route Pro CTA correctly
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();

  const handleProClick = () => {
    if (storedUser?.user_type === 'candidate') {
      // Already a candidate → straight to payment selection with chosen cycle
      navigate(`/plan-selection?plan=${billingCycle === 'annual' ? 'annual' : 'monthly'}`);
    } else if (storedUser?.user_type === 'organization') {
      // Orgs shouldn't subscribe to Professional - bounce to dashboard
      navigate('/organization-dashboard');
    } else {
      // Not logged in → signup with intent
      navigate(`/signup?type=candidate&plan=professional&cycle=${billingCycle}`);
    }
  };

  const handleFreeClick = () => {
    if (storedUser?.user_type === 'candidate') {
      navigate('/candidate-dashboard');
    } else if (storedUser?.user_type === 'organization') {
      navigate('/organization-dashboard');
    } else {
      navigate('/signup?type=candidate&plan=free');
    }
  };

  const handleEnterpriseClick = () => {
    if (storedUser?.user_type === 'organization') {
      navigate('/organization-dashboard');
    } else {
      navigate('/signup?type=organization');
    }
  };

  return (
    <div data-testid="pricing-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <span className="inline-block px-4 py-2 bg-white/10 border border-white/20 rounded-sm text-meritum-gold font-body text-sm font-medium mb-6">
            Simple & Scalable Pricing
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-none text-white mb-6">
            Choose Your Plan
          </h1>
          <p className="font-body text-lg md:text-xl text-slate-200 leading-relaxed max-w-2xl mx-auto">
            Start free, grow with confidence. Plans for candidates and organizations.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-14">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`font-body text-sm font-medium px-4 py-2 rounded-lg transition-colors ${billingCycle === 'monthly' ? 'bg-meritum-navy text-white' : 'text-slate-500 hover:text-meritum-navy'}`}
              data-testid="billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`font-body text-sm font-medium px-4 py-2 rounded-lg transition-colors relative ${billingCycle === 'annual' ? 'bg-meritum-navy text-white' : 'text-slate-500 hover:text-meritum-navy'}`}
              data-testid="billing-annual"
            >
              Annual
              <span className="absolute -top-3 -right-10 px-2 py-0.5 bg-meritum-gold text-meritum-navy text-[10px] font-bold rounded">SAVE 14%</span>
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Free Tier */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all duration-300" data-testid="free-tier">
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Zap size={20} className="text-slate-600" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-meritum-navy">Free</h3>
                </div>
                <p className="font-body text-sm text-slate-500 mb-6">Perfect for getting started</p>
                <div className="mb-8">
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-4xl font-bold text-meritum-navy">R0</span>
                    <span className="font-body text-slate-400">/forever</span>
                  </div>
                </div>
                <Button onClick={handleFreeClick} variant="outline" className="w-full border-2 border-meritum-navy text-meritum-navy hover:bg-meritum-navy hover:text-white font-body font-semibold py-5" data-testid="free-cta">
                  Get Started Free
                </Button>
              </div>
              <div className="px-8 pb-8">
                <p className="font-body text-xs font-medium text-slate-400 uppercase tracking-wider mb-4">What's included</p>
                <ul className="space-y-3">
                  {freeTierFeatures.map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                      <span className="font-body text-sm text-slate-600">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Professional Tier */}
            <div className="bg-white rounded-2xl border-2 border-meritum-gold shadow-xl overflow-hidden relative hover:shadow-2xl transition-all duration-300 md:-mt-4 md:mb-[-16px]" data-testid="pro-tier">
              <div className="absolute top-0 left-0 right-0 bg-meritum-gold text-meritum-navy text-center py-1.5">
                <span className="font-body text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1">
                  <Star size={12} fill="currentColor" /> Most Popular
                </span>
              </div>
              <div className="p-8 pt-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-meritum-gold/10 rounded-lg flex items-center justify-center">
                    <Shield size={20} className="text-meritum-gold" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-meritum-navy">Professional</h3>
                </div>
                <p className="font-body text-sm text-slate-500 mb-6">For serious candidates</p>
                <div className="mb-8">
                  {billingCycle === 'monthly' ? (
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading text-4xl font-bold text-meritum-navy">R35</span>
                      <span className="font-body text-slate-400">/month</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="font-heading text-4xl font-bold text-meritum-navy">R360</span>
                        <span className="font-body text-slate-400">/year</span>
                      </div>
                      <p className="font-body text-sm text-meritum-teal font-medium mt-1">R30/month, billed annually</p>
                    </>
                  )}
                </div>
                <Button onClick={handleProClick} className="w-full bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold py-5" data-testid="pro-cta">
                  Start Professional
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </div>
              <div className="px-8 pb-8">
                <p className="font-body text-xs font-medium text-slate-400 uppercase tracking-wider mb-4">Everything you need</p>
                <ul className="space-y-3">
                  {proTierFeatures.map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={16} className={`mt-0.5 flex-shrink-0 ${i === 0 ? 'text-meritum-teal' : 'text-meritum-gold'}`} />
                      <span className={`font-body text-sm ${i === 0 ? 'text-meritum-teal font-medium' : 'text-slate-600'}`}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Enterprise Tier */}
            <div className="bg-meritum-navy rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300" data-testid="enterprise-tier">
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-meritum-teal/20 rounded-lg flex items-center justify-center">
                    <Building2 size={20} className="text-meritum-gold" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white">Enterprise</h3>
                </div>
                <p className="font-body text-sm text-slate-400 mb-6">For organizations & recruiters</p>
                <div className="mb-8">
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-3xl font-bold text-white">Custom</span>
                  </div>
                  <p className="font-body text-sm text-slate-400 mt-1">Tailored to your needs</p>
                </div>
                <Button onClick={handleEnterpriseClick} variant="outline" className="w-full border-2 border-meritum-gold text-meritum-gold hover:bg-meritum-gold hover:text-meritum-navy font-body font-bold py-5" data-testid="enterprise-cta">
                  Start Enterprise
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </div>
              <div className="px-8 pb-8">
                <p className="font-body text-xs font-medium text-slate-500 uppercase tracking-wider mb-4">For teams & organizations</p>
                <ul className="space-y-3">
                  {enterpriseTierFeatures.map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={16} className="text-meritum-gold mt-0.5 flex-shrink-0" />
                      <span className="font-body text-sm text-slate-300">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 bg-meritum-gold/10 text-meritum-gold font-body text-sm font-medium rounded-sm mb-4">Our Values</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">What We Stand For</h2>
            <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed">Our core values guide everything we do at MeritumPraxis.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center p-8 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="w-16 h-16 bg-meritum-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="text-meritum-gold" size={28} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mb-3">{value.title}</h3>
                <p className="font-body text-sm text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-meritum-navy">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white tracking-tight mb-4">Ready to Get Started?</h2>
          <p className="font-body text-slate-300 mb-8 max-w-xl mx-auto">Join MeritumPraxis today and make your progress visible.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={handleProClick} className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-8 py-6 text-base">
              Create Your Profile
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
